/**
 * @file: useMessageReaders.ts
 *
 * The "read by" overlay — one responsibility. There's a single piece of
 * state: `readers$`. `null` means the sheet is closed; an array (possibly
 * empty) means it's open showing those readers. Opening fetches the readers
 * first, THEN publishes them, so the sheet appears with real data rather than
 * a flash of empties.
 */

import { ChatMessageReader, ChatMsg } from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useCreation } from '@euler/utils/hooks';
import { useCallback, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';

export interface MessageReaders {
  /** The readers being shown, or null when the sheet is closed. */
  readers$: BehaviorSubject<ChatMessageReader[] | null>;
  /** Fetch a (confirmed) message's readers, then open the sheet on them. */
  show: (message: ChatMsg) => void;
  close: () => void;
}

export function useMessageReaders(): MessageReaders {
  const { chatService } = useServiceFactory();
  const readers$ = useCreation(
    () => new BehaviorSubject<ChatMessageReader[] | null>(null),
    [],
  );

  const show = useCallback(
    (message: ChatMsg) => {
      // Only a confirmed (remote) message has readers.
      if (message.kind !== 'remote') return;
      chatService
        .listMessageReaders(message.messageId)
        .then(r => readers$.next(r.readers))
        .catch(onErrorIgnore);
    },
    [chatService, readers$],
  );

  const close = useCallback(() => readers$.next(null), [readers$]);

  return useMemo(() => ({ readers$, show, close }), [readers$, show, close]);
}
